import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import WavingFlag from "@/components/WavingFlag";
import Marquee from "@/components/Marquee";
import { Reveal, LineReveal } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";

const RALLY = "https://images.unsplash.com/photo-1774917894662-502070c9b626?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";
const CAPITOL = "https://images.unsplash.com/photo-1557160854-e1e89fdd3286?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function Home() {
  const { settings, policies, beliefs } = useContent();
  const s = settings || {};

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative flex min-h-screen items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <WavingFlag />
        </div>
        <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-r from-navy via-navy/75 to-navy/20" />
        <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-t from-navy via-transparent to-navy/40" />

        <div className="relative z-20 mx-auto w-full max-w-[1400px] px-5 pt-28 md:px-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-6 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-kinetic" />
            <span className="text-xs uppercase tracking-[0.32em] text-bone/70">
              {s.hero_overline || "An Independent Voice For America"}
            </span>
          </motion.div>

          <h1 className="max-w-4xl font-display text-[15vw] font-bold leading-[0.92] tracking-tighter text-bone sm:text-7xl lg:text-8xl">
            <LineReveal text={s.hero_heading || "A Stronger Future,\nBuilt Together."} delay={0.15} />
          </h1>

          <Reveal delay={0.6} className="mt-8 max-w-xl text-base leading-relaxed text-bone/70 md:text-lg">
            <p>{s.hero_subheading}</p>
          </Reveal>

          <Reveal delay={0.75} className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/donate"
              data-testid="hero-donate-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-kinetic px-8 py-4 text-sm font-semibold uppercase tracking-wider text-white transition-transform duration-300 hover:-translate-y-0.5"
            >
              {s.hero_cta_label || "Donate Now"}
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/policy"
              className="inline-flex items-center gap-3 rounded-full border border-bone/25 px-8 py-4 text-sm font-semibold uppercase tracking-wider text-bone transition-colors duration-300 hover:border-bone/60"
            >
              See the Platform
            </Link>
          </Reveal>
        </div>

        <div className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2 text-bone/40">
          <ArrowDown className="animate-bounce" size={22} />
        </div>
      </section>

      <Marquee items={s.marquee_items || []} />

      {/* THE MOVEMENT */}
      <section className="mx-auto max-w-[1400px] px-5 py-28 md:px-10 md:py-40">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.32em] text-kinetic">The Candidate</div>
          <h2 className="mt-6 font-display text-5xl font-bold leading-[0.95] tracking-tight text-bone md:text-6xl">
            Meet Mark Prascak
          </h2>
        </Reveal>

        <div className="mt-12 flex flex-col gap-10 md:flex-row md:items-start md:gap-14">
          <Reveal delay={0.1} className="w-full shrink-0 md:w-[240px]">
            <div className="relative">
              <div className="absolute -inset-3 -z-10 rounded-sm border border-hairline/60" />
              <img
                src={s.candidate_photo || RALLY}
                alt="Mark Prascak"
                className="aspect-[4/5] w-full rounded-sm object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 bg-kinetic px-4 py-2.5 font-display text-sm italic text-white">
                {s.tagline || "Country over party."}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="flex-1">
            <p className="whitespace-pre-line text-lg leading-relaxed text-bone/70">{s.bio}</p>
            <div className="mt-10">
              <Link to="/beliefs" className="link-underline inline-flex items-center gap-2 text-sm uppercase tracking-widest text-bone">
                Read our beliefs <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EDITORIAL NAV CARDS */}
      <section className="border-y border-hairline/50 bg-ink">
        <div className="mx-auto grid max-w-[1400px] md:grid-cols-3">
          {[
            { to: "/policy", label: "Policy", n: "01", desc: `${policies.length} clear positions on the issues that matter.` },
            { to: "/beliefs", label: "Beliefs", n: "02", desc: `${beliefs.length} principles that guide this campaign.` },
            { to: "/donate", label: "Donate", n: "03", desc: "Fuel a movement powered entirely by the people." },
          ].map((c, i) => (
            <Reveal key={c.to} delay={i * 0.1}>
              <Link
                to={c.to}
                data-testid={`home-card-${c.label.toLowerCase()}`}
                className="group flex h-full flex-col justify-between gap-16 border-hairline/50 p-10 transition-colors duration-500 hover:bg-navy md:min-h-[340px] md:border-l md:p-12 md:[&:first-child]:border-l-0"
              >
                <div className="flex items-start justify-between">
                  <span className="font-display text-6xl italic text-hairline transition-colors duration-500 group-hover:text-kinetic">
                    {c.n}
                  </span>
                  <ArrowRight className="text-bone/40 transition-all duration-500 group-hover:translate-x-1 group-hover:text-bone" />
                </div>
                <div>
                  <h3 className="font-display text-4xl font-bold tracking-tight text-bone">{c.label}</h3>
                  <p className="mt-3 max-w-xs text-sm text-bone/55">{c.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* DONATE CTA */}
      <section className="relative overflow-hidden">
        <img src={CAPITOL} alt="US Capitol" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="relative z-10 mx-auto max-w-[1000px] px-5 py-32 text-center md:py-44">
          <Reveal>
            <h2 className="font-display text-5xl font-bold leading-[0.95] tracking-tight text-bone md:text-7xl">
              This campaign runs on people, not PACs.
            </h2>
            <p className="mx-auto mt-8 max-w-xl text-lg text-bone/70">{s.donate_intro}</p>
            <Link
              to="/donate"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-kinetic px-10 py-4 text-sm font-semibold uppercase tracking-wider text-white transition-transform duration-300 hover:-translate-y-0.5"
            >
              Chip In Today <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
